import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Eye, Users, TrendingUp, Calendar, Copy, Download } from "lucide-react";
import { Button } from "./ui/button";

export default function AnalyticsPanel({ tournament }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get(
                `/tournaments/${tournament.id}/analytics`,
            );
            setData(data);
        } catch {
            toast.error("No se pudo cargar la audiencia");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tournament.id]);

    if (loading) {
        return (
            <div className="vp-card p-6 text-slate-500 text-sm">
                Cargando audiencia…
            </div>
        );
    }
    if (!data) return null;

    const maxHits = Math.max(1, ...(data.daily || []).map((d) => d.hits));

    const buildReport = () => {
        const lines = [
            `=== REPORTE DE AUDIENCIA · ${tournament.name} ===`,
            `Generado: ${new Date().toLocaleString()}`,
            ``,
            `Visitas totales:        ${data.total_hits}`,
            `Visitantes únicos:      ${data.unique_total}`,
            `Visitantes únicos 30d:  ${data.unique_30d}`,
            `Visitantes únicos hoy:  ${data.unique_today}`,
            ``,
            `Por sección:`,
            `  Estadísticas:         ${data.tabs.standings || 0}`,
            `  Rol de partidos:      ${data.tabs.rol || 0}`,
            `  Patrocinios:          ${data.tabs.sponsors || 0}`,
            ``,
            `Últimos días:`,
            ...(data.daily || []).map(
                (d) =>
                    `  ${d.day}:  ${String(d.hits).padStart(4)} vistas  ·  ${String(d.unique).padStart(3)} únicos`,
            ),
        ];
        return lines.join("\n");
    };

    const copyReport = () => {
        navigator.clipboard?.writeText(buildReport());
        toast.success("Reporte copiado");
    };

    const downloadReport = () => {
        const blob = new Blob([buildReport()], {
            type: "text/plain;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audiencia-${tournament.slug}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-5">
            <div className="vp-card p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Eye className="w-6 h-6 text-ipn-800" />
                        <div>
                            <h3 className="font-display text-xl font-bold">
                                Audiencia para patrocinadores
                            </h3>
                            <p className="text-sm text-slate-500">
                                Cifras reales de visibilidad de la página pública.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={copyReport}
                            className="rounded-xl"
                            data-testid="analytics-copy"
                        >
                            <Copy className="w-3 h-3 mr-1" />
                            Copiar
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={downloadReport}
                            className="rounded-xl"
                            data-testid="analytics-download"
                        >
                            <Download className="w-3 h-3 mr-1" />
                            Reporte
                        </Button>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                        label="Visitas totales"
                        value={data.total_hits}
                        icon={<Eye className="w-4 h-4" />}
                        accent="ipn"
                    />
                    <StatCard
                        label="Visitantes únicos"
                        value={data.unique_total}
                        icon={<Users className="w-4 h-4" />}
                        accent="yellow"
                    />
                    <StatCard
                        label="Últimos 30 días"
                        value={data.unique_30d}
                        icon={<TrendingUp className="w-4 h-4" />}
                        sub="visitantes únicos"
                    />
                    <StatCard
                        label="Hoy"
                        value={data.unique_today}
                        icon={<Calendar className="w-4 h-4" />}
                        sub="visitantes únicos"
                    />
                </div>

                {/* Daily sparkline */}
                <div className="mt-6">
                    <Label>Últimos 14 días</Label>
                    {data.daily.length === 0 ? (
                        <p className="text-sm text-slate-500 mt-2">
                            Aún no hay datos. Comparte el link público para
                            empezar a contar.
                        </p>
                    ) : (
                        <div className="mt-3 grid grid-cols-7 sm:grid-cols-14 gap-1 items-end h-32">
                            {data.daily.map((d) => {
                                const h = Math.max(
                                    4,
                                    Math.round((d.hits / maxHits) * 120),
                                );
                                return (
                                    <div
                                        key={d.day}
                                        className="flex flex-col items-center justify-end gap-1"
                                        title={`${d.day}: ${d.hits} vistas · ${d.unique} únicos`}
                                    >
                                        <div
                                            className="w-full bg-ipn-800 rounded-t-md hover:bg-ipn-900 transition-all"
                                            style={{ height: `${h}px` }}
                                        />
                                        <span className="text-[9px] text-slate-400 font-bold">
                                            {d.day.slice(8, 10)}/
                                            {d.day.slice(5, 7)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Tab breakdown */}
                <div className="mt-6">
                    <Label>Vistas por sección (acumuladas)</Label>
                    <div className="mt-3 space-y-2">
                        <TabBar
                            label="Estadísticas"
                            v={data.tabs.standings || 0}
                            total={
                                (data.tabs.standings || 0) +
                                (data.tabs.rol || 0) +
                                (data.tabs.sponsors || 0)
                            }
                        />
                        <TabBar
                            label="Rol de partidos"
                            v={data.tabs.rol || 0}
                            total={
                                (data.tabs.standings || 0) +
                                (data.tabs.rol || 0) +
                                (data.tabs.sponsors || 0)
                            }
                        />
                        <TabBar
                            label="Patrocinios"
                            v={data.tabs.sponsors || 0}
                            total={
                                (data.tabs.standings || 0) +
                                (data.tabs.rol || 0) +
                                (data.tabs.sponsors || 0)
                            }
                        />
                    </div>
                </div>

                <p className="text-xs text-slate-500 mt-5 leading-relaxed">
                    💡 Tip: comparte el reporte con tus patrocinadores para
                    justificar el costo del logo. Los visitantes únicos cuentan
                    1 vez por día (incluso si recargan la página).
                </p>
            </div>
        </div>
    );
}

function Label({ children }) {
    return (
        <div className="text-xs uppercase font-bold tracking-widest text-slate-400">
            {children}
        </div>
    );
}

function StatCard({ label, value, icon, accent = "slate", sub }) {
    const accentClasses = {
        ipn: "bg-ipn-50 text-ipn-900 border-ipn-100",
        yellow: "bg-ipnYellow/30 text-ipn-950 border-ipnYellow/40",
        slate: "bg-slate-50 text-slate-900 border-slate-100",
    };
    return (
        <div
            className={`rounded-2xl border p-4 ${
                accentClasses[accent] || accentClasses.slate
            }`}
        >
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest opacity-60">
                {icon}
                {label}
            </div>
            <div className="mt-1 font-display text-3xl font-black">{value}</div>
            {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
        </div>
    );
}

function TabBar({ label, v, total }) {
    const pct = total > 0 ? Math.round((v / total) * 100) : 0;
    return (
        <div className="text-sm">
            <div className="flex justify-between mb-1">
                <span className="font-semibold">{label}</span>
                <span className="text-slate-500">
                    {v} ({pct}%)
                </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                    className="h-full bg-ipn-800 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}
